/**
 * useMediaQuery — reactive `window.matchMedia` as a boolean.
 *
 * Safe where matchMedia is missing (SSR, jsdom): returns `defaultMatches`
 * and never subscribes. Used to decide what to RENDER for a breakpoint
 * (not just what to hide with CSS) when hidden DOM would otherwise
 * swallow interactions — e.g. the dashboard's compact Action Center.
 */
import { useEffect, useState } from "react";

/** Tailwind `lg` — the breakpoint where the desktop sidebar appears. */
export const DESKTOP_QUERY = "(min-width: 1024px)";

function read(query, defaultMatches) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return defaultMatches;
  }
  try {
    return !!window.matchMedia(query).matches;
  } catch {
    return defaultMatches;
  }
}

export function useMediaQuery(query, { defaultMatches = false } = {}) {
  const [matches, setMatches] = useState(() => read(query, defaultMatches));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = (e) => setMatches(!!e.matches);
    setMatches(!!mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    if (typeof mql.addListener === "function") {
      mql.addListener(onChange);
      return () => mql.removeListener(onChange);
    }
    return undefined;
  }, [query]);

  return matches;
}
